/**
 * /admin/produtos/conciliar — fila de revisão do catálogo único
 * (R-IMPORT 3 A1.10 / Camada 5).
 *
 * Lista as sugestões em que o sistema tem confiança média (0,6 a 0,85) de que
 * dois produtos de CNPJs diferentes são o mesmo. O admin aprova (junta os dois
 * no catálogo único) ou rejeita (a sugestão não volta).
 *
 * FASE-C (redesign): "Conciliar Produtos Cross-CNPJ", "Camada 5" e os nomes de
 * tabela saíram da tela; os botões de filtro pintados com `blue-500` viraram
 * `Tabs` canônico (mesmos `data-testid`); `bg-white` e `red-100/200` viraram
 * token; a comparação lado a lado ganhou `min-w-0` + `break-words` pra não
 * estourar no celular de 375px.
 */
import { useEffect, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { api } from '@/lib/api';

interface ReviewItem {
  id: string;
  empresa_a_id: string;
  codigo_produto_a: string;
  empresa_b_id: string;
  codigo_produto_b: string;
  confianca_match: number;
  motivo: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  criado_em_iso: string;
  desc_a: string | null;
  cod_a: string | null;
  desc_b: string | null;
  cod_b: string | null;
}

const FILTROS = [
  { valor: 'pendente', label: 'Esperando revisão' },
  { valor: 'aprovado', label: 'Aprovadas' },
  { valor: 'rejeitado', label: 'Rejeitadas' },
] as const;

const VAZIO: Record<ReviewItem['status'], string> = {
  pendente: 'Nenhuma sugestão esperando revisão.',
  aprovado: 'Nenhuma sugestão aprovada até agora.',
  rejeitado: 'Nenhuma sugestão rejeitada até agora.',
};

export function ProdutosConciliarPage() {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<'pendente' | 'aprovado' | 'rejeitado'>(
    'pendente',
  );
  const [err, setErr] = useState<string | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);

  function load() {
    setErr(null);
    api
      .get<{ items: ReviewItem[] }>(
        `/api/v1/admin/produtos/conciliar?status=${statusFilter}`,
      )
      .then((r) => setItems(r.items))
      .catch((e: Error) => setErr(e.message));
  }
  useEffect(load, [statusFilter]);

  async function handleAcao(id: string, acao: 'aprovar' | 'rejeitar') {
    setProcessing(id);
    try {
      await api.post(`/api/v1/admin/produtos/conciliar/${id}`, { acao });
      load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setProcessing(null);
    }
  }

  return (
    <div className="min-w-0 space-y-5" data-testid="produtos-conciliar-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Conciliação de produtos"
        subtitle="Quando o sistema suspeita que dois produtos de CNPJs diferentes são o mesmo, mas não tem certeza, a sugestão para aqui pra alguém decidir."
      />

      <Alert variant="info" title="O que cada botão faz">
        <strong>Aprovar</strong> junta os dois cadastros num único produto do catálogo.{' '}
        <strong>Rejeitar</strong> descarta a sugestão e evita que ela volte a aparecer.
      </Alert>

      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as ReviewItem['status'])}>
        <TabsList>
          {FILTROS.map((f) => (
            <TabsTrigger key={f.valor} value={f.valor} data-testid={`filter-${f.valor}`}>
              {f.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {err && (
        <Alert variant="danger" title="Não foi possível carregar as sugestões">
          Tente recarregar a página. Se continuar, avise quem cuida do servidor.
          <span className="mt-1 block break-words text-xs opacity-80">Detalhe: {err}</span>
        </Alert>
      )}

      {items.length === 0 ? (
        <Card variant="flat" data-testid="empty-state">
          <EmptyState
            title={VAZIO[statusFilter]}
            description="As sugestões aparecem depois de uma sincronização de produtos com o Omie."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card
              key={it.id}
              variant="flat"
              className="p-4 space-y-4"
              data-testid={`review-${it.id}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <div className="break-words text-xs text-ink-3 dark:text-ink-dark-3">
                    {it.empresa_a_id}
                  </div>
                  <div className="break-words font-semibold text-ink-1 dark:text-ink-dark-1">
                    {it.desc_a ?? '(sem descrição)'}
                  </div>
                  <div className="break-words text-xs text-ink-2 dark:text-ink-dark-2">
                    Código: <code className="font-mono">{it.cod_a ?? '—'}</code> · Código no
                    Omie: <code className="font-mono">{it.codigo_produto_a}</code>
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="break-words text-xs text-ink-3 dark:text-ink-dark-3">
                    {it.empresa_b_id}
                  </div>
                  <div className="break-words font-semibold text-ink-1 dark:text-ink-dark-1">
                    {it.desc_b ?? '(sem descrição)'}
                  </div>
                  <div className="break-words text-xs text-ink-2 dark:text-ink-dark-2">
                    Código: <code className="font-mono">{it.cod_b ?? '—'}</code> · Código no
                    Omie: <code className="font-mono">{it.codigo_produto_b}</code>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="min-w-0 break-words text-xs text-ink-3 dark:text-ink-dark-3">
                  Semelhança de{' '}
                  <strong className="font-mono">
                    {(it.confianca_match * 100).toFixed(1)}%
                  </strong>{' '}
                  · {it.motivo}
                </div>
                {it.status === 'pendente' ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleAcao(it.id, 'rejeitar')}
                      disabled={processing === it.id}
                      data-testid={`reject-${it.id}`}
                    >
                      Rejeitar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAcao(it.id, 'aprovar')}
                      disabled={processing === it.id}
                      data-testid={`approve-${it.id}`}
                    >
                      Aprovar
                    </Button>
                  </div>
                ) : (
                  <Badge
                    variant={it.status === 'aprovado' ? 'success' : 'danger'}
                    size="sm"
                  >
                    {it.status === 'aprovado' ? 'Aprovada' : 'Rejeitada'}
                  </Badge>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
